import type { IAppStores } from "./AppMaster";
import { Fetcher } from "../util/io/Fetcher";
import { ResourcesFetcher } from "../network/fetchers/ResourcesFetcher";
import { ResourcesStore } from "../resources/ResourcesStore";
import { POW3Fetchers } from "../network/fetchers/POW3Fetchers";
import { NewsMenuStore } from "./header/news-menu/NewsMenuStore";
import { RichToolTipStore } from "./rich-tooltip/RichToolTipStore";
import { AboutUsDialogStore } from "./header/help-menu/about-us/AboutUsDialogStore";
import { HeartBeatFetcher } from "../network/fetchers/HeartBeatFetcher";
import { DefaultCurrentWindowStore } from "./window/DefaultCurrentWindowStore";
import { CurrentDocumentStore } from "./window/document/CurrentDocumentStore";
import { HeartBeatStore } from "./heartbeat/HeartBeatStore";
import { UserSessionStore } from "./user/UserSessionStore";
import { ProductInfoStore } from "./product-info/ProductInfoStore";
import type { IGenericIndexViewModel } from "../network/contracts/IGenericIndexViewModel";
import { CookieSettingsStore } from "./cookie-banner/CookieSettingsStore";
import type { Language } from "../util/language/language";
import { ThirdPartySoftwareStore } from "./header/help-menu/about-us/ThirdPartySoftwareStore";
import { NewsItemMapper } from "../app-upload/network/mappers/NewsItemMapper";
import { OtherEnvironmentsStore } from "./environments/OtherEnvironmentsStore";
import { CurrentEnvironmentStore } from "./environments/CurrentEnvironmentStore";

export interface IAppStoresFactory {
    create(genericViewModel: IGenericIndexViewModel): Promise<IAppStores>;
}

export class AppStoresFactory implements IAppStoresFactory {
    public async create(genericViewModel: IGenericIndexViewModel): Promise<IAppStores> {
        const { productInfo, userSession } = genericViewModel;

        const currentWindowStore = new DefaultCurrentWindowStore();
        const currentDocumentStore = new CurrentDocumentStore(currentWindowStore);
        const fetcher = new Fetcher();
        const pow3Fetchers = new POW3Fetchers(fetcher);
        const resourcesFetcher = new ResourcesFetcher(fetcher, productInfo.version);
        const resources = await resourcesFetcher.fetch();
        const resourcesStore = new ResourcesStore(
            resources.resources as unknown as IResources,
            resources.language as Language,
            resources.possibleLanguages as Language[],
            resourcesFetcher,
        );
        
        const currentEnvironmentStore = new CurrentEnvironmentStore(pow3Fetchers);
        await currentEnvironmentStore.getCurrentEnvironment();
        const otherEnvironmentsStore = new OtherEnvironmentsStore(pow3Fetchers);
        await otherEnvironmentsStore.getOtherActiveEnvironments();
        
        const richToolTipStore = new RichToolTipStore();
        const productInfoStore = new ProductInfoStore(productInfo, currentEnvironmentStore);

        const newsItemMapper = new NewsItemMapper();
        const newsMenuStore = new NewsMenuStore(pow3Fetchers, newsItemMapper);

        const aboutUsDialogStore = new AboutUsDialogStore();
        const userSessionStore = new UserSessionStore(userSession);
        const heartBeatFetcher = new HeartBeatFetcher(fetcher);
        const heartBeatStore = new HeartBeatStore(heartBeatFetcher, currentWindowStore, userSessionStore);
        const cookieSettingsStore = new CookieSettingsStore(userSessionStore);
        const thirdPartySoftwareStore = new ThirdPartySoftwareStore();

        return {
            productInfoStore,
            resourcesStore,
            richToolTipStore,
            newsMenuStore,
            currentEnvironmentStore,
            otherEnvironmentsStore,
            aboutUsDialogStore,
            heartBeatStore,
            currentWindowStore,
            currentDocumentStore,
            userSessionStore,
            cookieSettingsStore,
            thirdPartySoftwareStore,
        };
    }
}
